import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { copyToClipboard } from '@/shared/utils/exportUtils';

const LANG_LABELS = {
  javascript: 'JavaScript',
  js: 'JavaScript',
  jsx: 'JSX',
  bash: 'Bash',
  sql: 'SQL',
  json: 'JSON',
  env: 'ENV',
};

function CodeBlock({ language, children }) {
  const [copied, setCopied] = useState(false);
  const code = String(children).replace(/\n$/, '');
  const label = LANG_LABELS[language] || (language ? language.toUpperCase() : 'Code');

  const handleCopy = async () => {
    const result = await copyToClipboard(code);
    if (result.success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="group relative rounded-lg border border-gray-700 bg-gray-900 my-4 not-prose">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-700/60 bg-gray-800/50 rounded-t-lg">
        <span className="text-xs font-medium text-gray-400 select-none">{label}</span>
        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 transition-colors"
        >
          {copied ? (
            <>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Copied
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy
            </>
          )}
        </button>
      </div>
      <pre className="overflow-x-auto p-4 text-[13px] leading-relaxed">
        <code className="text-gray-100 font-mono">{code}</code>
      </pre>
    </div>
  );
}

const markdownComponents = {
  code({ className, children, ...props }) {
    return (
      <code className="text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
        {children}
      </code>
    );
  },
  pre({ children }) {
    const codeChild = children?.props;
    if (codeChild) {
      const match = /language-(\w+)/.exec(codeChild.className || '');
      return <CodeBlock language={match ? match[1] : ''}>{codeChild.children}</CodeBlock>;
    }
    return <pre>{children}</pre>;
  },
};

export default function AdminMarkdownDoc({ icon, title, description, subtitle, markdown }) {
  const [copied, setCopied] = useState(false);

  const handleCopyMarkdown = async () => {
    const result = await copyToClipboard(markdown);
    if (result.success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <span>{icon}</span>
              {title}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{description}</p>
            {subtitle && (
              <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
            )}
          </div>
          <button
            onClick={handleCopyMarkdown}
            className={`
              inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0
              ${copied
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }
            `}
          >
            {copied ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy as Markdown
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 sm:p-8">
        <article className="prose prose-sm sm:prose-base max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900 prose-table:text-sm prose-th:bg-gray-50 prose-th:px-4 prose-th:py-2 prose-td:px-4 prose-td:py-2 prose-th:border prose-th:border-gray-200 prose-td:border prose-td:border-gray-200 prose-hr:border-gray-200 prose-a:text-purple-600 prose-a:no-underline hover:prose-a:underline">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {markdown}
          </ReactMarkdown>
        </article>
      </div>
    </div>
  );
}
